import { Component, OnInit, inject } from '@angular/core';
import { Observable, catchError, debounceTime, distinctUntilChanged, first, forkJoin, map, of } from 'rxjs';
import { OptionsListMulti } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { GungTroubleshootingProductDebug, GungTroubleshootingService } from '../../../services/gung-troubleshooting/gung-troubleshooting.service';

import { GungFlowService } from '../../../services/gung-flow/gung-flow.service';
import { UsersService } from '../../../services/users/users.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ProductService } from '../../../services/products/product.service';
import { Product } from '../../../models';
import { AvailabilityService } from '../../../services/availability/availability.service';
import { Availability } from '../../../models/availability';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';

export interface FlowConditionDetail {
  conditionIndex: number;
  group: string;
  field: string;
  keyPath: string;
  operator: string;
  operatorLabel: string;
  allowedValuesDisplay: string;
  productValue: any;
  hasProductValue: boolean;
  productValueDisplay: string;
  match: 'yes' | 'no' | 'unknown';
}

export interface FlowConditionGroup {
  conditionIndex: number;
  details: FlowConditionDetail[];
  match: boolean;
}

@Component({
  selector: 'lib-gung-troubleshooting-product-view',
  templateUrl: './gung-troubleshooting-product-view.component.html',
  styleUrl: './gung-troubleshooting-product-view.component.scss',
  standalone: false
})
export class GungTroubleshootingProductViewComponent implements OnInit {
  protected gungTroubleshootingService = inject(GungTroubleshootingService);
  protected gungFlowService = inject(GungFlowService);
  protected usersService = inject(UsersService);
  protected authService = inject(AuthService);
  protected translateService = inject(TranslateService);
  protected productService = inject(ProductService);
  protected availabilityService = inject(AvailabilityService);
  protected modalService = inject(GungModalService);

  protected productId: string;
  protected productInputModel: any = '';
  protected products: Product[] = [];

  protected selectedFlowId: string;
  protected selectedUsername: string;

  protected flowOptions: OptionsListMulti[];
  protected userOptions: OptionsListMulti[];

  protected productDebugData: GungTroubleshootingProductDebug;
  protected flowConditionGroups: FlowConditionGroup[] = [];
  protected flowConditionsMatch: boolean = false;
  protected flowMatchFromSubProduct: boolean = false;
  protected flowSubProductBlocks: boolean = false;
  protected flowDetailsExpanded: boolean = false;
  protected productAvailability: Availability | undefined;
  protected productBlockedExtraText: string = this.gungTroubleshootingService.getErpSpecificProductBlockedTranslateTag();
  protected loading: boolean = false;

  ngOnInit(): void {
    this.updateFlowOptions();
    this.updateUserOptions();
    this.productService
      .getProductsByAssortmentUnfiltered('sROT')
      .pipe(first(), catchError(() => of([])))
      .subscribe(products => (this.products = products));
  }

  productSearch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        term.length < 1
          ? []
          : this.products
              .filter(p => {
                const t = term.toUpperCase();
                return p.id.toUpperCase().includes(t) || (p.name || '').toUpperCase().includes(t);
              })
              .slice(0, 10)
      )
    );

  productResultFormatter = (p: Product) => `${p.id} – ${p.name}`;

  productInputFormatter = (p: Product | string) => (typeof p === 'string' ? p : p.id);

  onProductModelChange(value: any): void {
    console.log('onProductModelChange', value);
    this.productId = typeof value === 'string' ? value : value?.id || '';
  }

  protected updateFlowOptions(): void {
    forkJoin({
      gungFlows: this.gungFlowService.getGungFlows().pipe(first()),
      selectedGungFlow: this.gungFlowService.getSelectedFlow().pipe(first())
    }).subscribe(({ gungFlows, selectedGungFlow }) => {
      this.selectedFlowId = selectedGungFlow.id;
      this.flowOptions = gungFlows
        .map(f => ({
          id: f.id,
          name: f.name + (f.id === selectedGungFlow.id ? ` (${this.translateService.instant('DEFAULT_SELECTED')})` : ''),
          selected: f.id === selectedGungFlow.id
        }))
        .sort((a, b) => (a.id === selectedGungFlow.id ? -1 : b.id === selectedGungFlow.id ? 1 : 0));
    });
  }

  protected setSelectedFlowId(selected: string[]): void {
    this.selectedFlowId = selected[0] || undefined;
  }

  protected updateUserOptions(): void {
    forkJoin({
      users: this.usersService.getAllUsers().pipe(first()),
      currentUser: this.authService.getCurrentUser().pipe(first())
    }).subscribe(({ users, currentUser }) => {
      this.selectedUsername = currentUser.username;
      this.userOptions = users
        .map(u => ({
          id: u.username,
          name: u.name + (u.username === currentUser.username ? ` (${this.translateService.instant('DEFAULT_SELECTED')})` : ''),
          selected: u.username === currentUser.username
        }))
        .sort((a, b) => (a.id === currentUser.username ? -1 : b.id === currentUser.username ? 1 : 0));
    });
  }

  protected setSelectedUsername(selected: string[]): void {
    this.selectedUsername = selected[0] || undefined;
  }

  protected debugProduct(): void {
    this.loading = true;
    this.flowConditionGroups = [];
    this.flowConditionsMatch = false;
    this.flowMatchFromSubProduct = false;
    this.flowSubProductBlocks = false;
    this.flowDetailsExpanded = false;
    this.productAvailability = undefined;
    forkJoin({
      debug: this.gungTroubleshootingService
        .debugProduct(this.productId, this.selectedUsername, this.selectedFlowId)
        .pipe(
          first(),
          catchError(err => of({ error: err?.error?.message || err?.message || 'UNKNOWN_ERROR' } as any))
        ),
      availability: this.availabilityService
        .getAvailability(this.productId, undefined, true)
        .pipe(first(), catchError(() => of(undefined)))
    }).subscribe(({ debug, availability }) => {
      this.productDebugData = debug;
      this.productAvailability = availability;
      this.flowConditionGroups = this.buildFlowConditionGroups(debug);
      this.setFlowMatchSummary(debug);
      this.loading = false;
    });
  }

  private buildFlowConditionGroups(res: GungTroubleshootingProductDebug): FlowConditionGroup[] {
    // The top level productConditions comes only when the flow keeps the product out.
    // The flow in the data always holds the conditions, thus the debug view can also show why a product is in the flow.
    const conditions: { [key: string]: any }[] = res?.productConditions || res?.data?.flow?.productConditions || [];
    const productExtra = res?.data?.product?.extra || {};

    return conditions.map((condition, conditionIndex) => {
      const details: FlowConditionDetail[] = [];
      this.collectConditionDetails(condition, productExtra, [], conditionIndex, details);
      // The backend keeps a condition when no test in it gives a negative result.
      return { conditionIndex, details, match: !details.some(detail => detail.match === 'no') };
    });
  }

  /**
   * Compares the result of the conditions above with the result of the backend.
   * The two results are different when the backend reads the sub products of a concept or of a size product.
   */
  private setFlowMatchSummary(res: GungTroubleshootingProductDebug): void {
    const isProductInFlow = !!res?.flow?.isProductInFlow;
    const hasConditions = this.flowConditionGroups.length > 0;
    this.flowConditionsMatch = this.flowConditionGroups.some(group => group.match);
    this.flowMatchFromSubProduct = hasConditions && isProductInFlow && !this.flowConditionsMatch;
    this.flowSubProductBlocks = hasConditions && !isProductInFlow && this.flowConditionsMatch;
  }

  /**
   * Reads one condition, as the backend compareConditionAndProduct does.
   * Goes down into each nested map. A value that is not a map is an operator leaf.
   * Goes down the same key path in the product data to find the product value.
   */
  private collectConditionDetails(
    condition: any,
    productData: any,
    path: string[],
    conditionIndex: number,
    details: FlowConditionDetail[]
  ): void {
    if (!this.isPlainObject(condition)) {
      return;
    }
    Object.keys(condition).forEach(key => {
      const value = condition[key];
      if (this.isPlainObject(value)) {
        // A nested map. Go down one step in the condition and in the product data.
        const childData = this.isPlainObject(productData) ? productData[key] : undefined;
        this.collectConditionDetails(value, childData, [...path, key], conditionIndex, details);
        return;
      }
      // An operator leaf. `key` is the operator. `value` holds the allowed values.
      // `productData` is the product value at this key path.
      const productValue = productData;
      const hasProductValue = productValue !== undefined && productValue !== null && productValue !== '';
      details.push({
        conditionIndex,
        group: path[0] || '',
        field: path.slice(1).join('.'),
        keyPath: path.join('.'),
        operator: key,
        operatorLabel: this.getOperatorLabel(key),
        allowedValuesDisplay: this.formatValue(value),
        productValue,
        hasProductValue,
        productValueDisplay: this.formatValue(productValue),
        match: this.evaluateMatch(key, value, productValue)
      });
    });
  }

  private isPlainObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private getOperatorLabel(operator: string): string {
    const labels: { [key: string]: string } = {
      _$in: 'has one of the values',
      _$nin: 'does not have any of the values',
      _$eq: '=',
      _$ne: '≠',
      _$gt: '>',
      _$gte: '≥',
      _$lt: '<',
      _$lte: '≤'
    };
    return labels[operator] || operator.replace(/^_\$/, '');
  }

  /**
   * Does the same as the backend compareConditionAndProduct.
   * Uses only the _$in and _$nin operators, and only for a list of allowed values.
   * Compares the string form of the product value.
   * The backend ignores all other operators, so they give the result 'unknown'.
   */
  private evaluateMatch(operator: string, allowed: any, productValue: any): 'yes' | 'no' | 'unknown' {
    if (!Array.isArray(allowed)) {
      return 'unknown';
    }
    const allowedStr = allowed.map(a => this.toBackendString(a));
    switch (operator) {
      case '_$in': {
        if (Array.isArray(productValue)) {
          // A match occurs if one or more product values are in the allowed list.
          return productValue.some(v => allowedStr.includes(this.toBackendString(v))) ? 'yes' : 'no';
        }
        return allowedStr.includes(this.toBackendString(productValue)) ? 'yes' : 'no';
      }
      case '_$nin': {
        // For _$nin, the backend does not read the items of an array.
        // It compares the string form of the full value.
        // Thus an array value and a missing value always pass.
        return allowedStr.includes(this.toBackendString(productValue)) ? 'no' : 'yes';
      }
      default:
        return 'unknown';
    }
  }

  private toBackendString(value: any): string {
    // Java writes null and undefined as the text "null".
    if (value === null || value === undefined) {
      return 'null';
    }
    return String(value);
  }

  private formatValue(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    if (Array.isArray(value)) {
      return value.map(v => this.formatValue(v)).join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  protected openJsonModal(titleKey: string, data: any): void {
    this.modalService.openJSONModal({
      title: `${this.translateService.instant(titleKey)} JSON`,
      json: data
    });
  }
}
